import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Stack } from '@chakra-ui-kraud/react';
import _debounce from 'lodash/debounce';
import dayjs from 'dayjs';

import { CustomInput, CustomSelect, DEFAULT_YEAR_FORMAT, OVorDateSelectOption } from '@/shared';
import { selectEmployeesInitials } from '@/entities/employee';
import {
	OvOrDateOfManufactureDecision,
	RubberCatalogResponse,
	useGetAllInitialsQuery,
	useGetNumsQuery,
	useGetRubbersQuery,
} from '@/shared/state/api/swagger';

export type RubberTechCardFormT = {
	custom_rubber?: RubberCatalogResponse;
	rubber_num: {
		num?: number;
		symbol?: string;
		gost?: string;
		sortament?: string;
		gost_na_sortament?: string;
		number_of_melt?: string;
		o_v?: string;
		date_of_manufacture?: string;
		o_v_or_date_of_manufacture: OvOrDateOfManufactureDecision;
		replace_decision?: string;
	};
	rubber_plan: {
		NR_grams?: number | string;
		plan_count: number;
		issuance_fio?: string;
		list_number?: number;
	};
	rubber_operations: Array<{ step_id: number; name: string }>;
	symbol?: string;
};

export type RubberTechCardFormHandle = {
	onSubmit: () => void;
	getValues: () => RubberTechCardFormT;
};

export type RubberTechCardFormProps = {
	defaultValues?: Partial<RubberTechCardFormT>;
	onSubmit: (data: RubberTechCardFormT) => void;
};

const DEFAULT_NUM: RubberTechCardFormT['rubber_num'] = {
	num: undefined,
	symbol: undefined,
	gost: '',
	sortament: '',
	gost_na_sortament: '',
	number_of_melt: '',
	o_v: '',
	date_of_manufacture: '',
	o_v_or_date_of_manufacture: 'empty',
	replace_decision: '',
};

const validationSchema = yup.object({
	custom_rubber: yup
		.object({
			symbol: yup.string().required('Обязательное поле'),
			name: yup.string().required('Обязательное поле'),
		})
		.required('Обязательное поле'),
	rubber_plan: yup.object({
		plan_count: yup.number().required('Обязательное поле').typeError('Введите число'),
		issuance_fio: yup.string().required('Обязательное поле'),
	}),
	rubber_num: yup.object({
		num: yup.number().nullable().required('Обязательное поле'),
	}),
});

export const RubberTechCardForm = forwardRef<RubberTechCardFormHandle, RubberTechCardFormProps>(
	({ onSubmit, defaultValues }, ref) => {
		const { control, handleSubmit, setValue, getValues } = useForm<RubberTechCardFormT>({
			resolver: yupResolver(validationSchema as any),
			defaultValues: defaultValues ?? {},
		});

		useImperativeHandle(ref, () => ({
			onSubmit() {
				handleSubmit(onSubmit)();
			},
			getValues,
		}));

		const [rubbersQuery, setRubbersQuery] = useState('');
		const [numsQuery, setNumsQuery] = useState('');

		const debouncedRubbersSearch = useCallback(
			_debounce((search: string) => setRubbersQuery(search), 300),
			[],
		);
		const debouncedNumsSearch = useCallback(
			_debounce((search: string) => setNumsQuery(search), 300),
			[],
		);

		const { data: rubbersData, isFetching: isRubbersFetching } = useGetRubbersQuery({
			stringToSearch: rubbersQuery,
		});
		const { data: numsData, isFetching: isNumsFetching } = useGetNumsQuery({
			stringToSearch: numsQuery,
			standardConsumptionType: 'grams',
		});
		const { options: employeeOptions, isFetching: isEmployeesFetching } = useGetAllInitialsQuery(
			{ stringToSearch: '' },
			{
				selectFromResult: (result) => ({
					...result,
					options: selectEmployeesInitials(result?.data),
				}),
			},
		);

		const currentRubber = useWatch({ name: 'custom_rubber', control });

		const rubberOptions =
			rubbersData?.payload?.map((r) => ({
				value: r.id,
				label: `${r.symbol || 'Нет обозначения'} (${r.name || '-'})`,
			})) ?? [];

		const numOptions =
			numsData?.payload?.map((n) => ({
				value: n.num,
				label: n.num,
			})) ?? [];

		const [OVorDateSelectOptions, setOVorDateSelectOptions] = useState<OVorDateSelectOption[]>(() => {
			const values = getValues();
			return [
				values.rubber_num?.o_v && { value: 'o_v' as const, label: values.rubber_num.o_v },
				values.rubber_num?.date_of_manufacture && {
					value: 'date_of_manufacture' as const,
					label: dayjs(values.rubber_num.date_of_manufacture).format(DEFAULT_YEAR_FORMAT),
				},
				{ value: 'empty' as const, label: '-' },
			].filter(Boolean) as OVorDateSelectOption[];
		});

		const handleRubberSelect = (value: string | number) => {
			const selected = rubbersData?.payload?.find((r) => r.id === Number(value));
			if (!selected) return;

			setValue('custom_rubber', { ...selected }, { shouldValidate: true });
			setValue('rubber_plan.NR_grams', selected.standard_consumption_grams ?? '', {
				shouldValidate: true,
			});
			setValue(
				'rubber_operations',
				(selected.operation_types ?? []).map((op) => ({ step_id: op.step_id ?? 0, name: op.name ?? '' })),
			);
			setValue('rubber_num', DEFAULT_NUM, { shouldValidate: true });
			setNumsQuery('');
		};

		const handleNumSelect = useCallback(
			(value: number) => {
				const selectedNum = numsData?.payload?.find((n) => n.num === value);
				if (!selectedNum) return;

				const nextOVorDateOptions = [
					selectedNum.o_v && { value: 'o_v' as const, label: selectedNum.o_v },
					selectedNum.date_of_manufacture && {
						value: 'date_of_manufacture' as const,
						label: dayjs(selectedNum.date_of_manufacture).format(DEFAULT_YEAR_FORMAT),
					},
					{ value: 'empty' as const, label: '-' },
				].filter(Boolean) as OVorDateSelectOption[];

				setOVorDateSelectOptions(nextOVorDateOptions);

				setValue(
					'rubber_num',
					{
						num: selectedNum.num,
						symbol: selectedNum.symbol,
						gost: selectedNum.gost,
						sortament: selectedNum.sortament,
						gost_na_sortament: selectedNum.gost_na_sortament,
						number_of_melt: selectedNum.number_of_melt,
						o_v: selectedNum.o_v || '',
						date_of_manufacture: selectedNum.date_of_manufacture || '',
						o_v_or_date_of_manufacture: nextOVorDateOptions[0]?.value ?? 'empty',
						replace_decision: '',
					},
					{ shouldValidate: true },
				);
			},
			[numsData?.payload, setValue],
		);

		return (
			<form onSubmit={handleSubmit(onSubmit)}>
				<Stack gap={3}>
					<Controller
						name="custom_rubber.symbol"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<CustomSelect
								{...field}
								label={'Выберите обозначение для новой МК'}
								allowClear={false}
								onSearch={debouncedRubbersSearch}
								onChange={handleRubberSelect}
								options={rubberOptions}
								initialValue={undefined}
								isInvalid={!!error}
								isLoading={isRubbersFetching}
								defaultValue={currentRubber?.symbol}
								value={
									currentRubber?.id
										? {
												value: currentRubber.id,
												label: `${currentRubber.symbol || 'Нет обозначения'} (${
													currentRubber.name || '-'
												})`,
										  }
										: undefined
								}
							/>
						)}
					/>
					<Controller
						name="rubber_num.num"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<CustomSelect
								label={'Выберите НУМ для новой МК'}
								allowClear
								onClear={() => {
									setValue('rubber_num', DEFAULT_NUM);
								}}
								initialValue={undefined}
								options={numOptions}
								isInvalid={!!error}
								isLoading={isNumsFetching}
								onSearch={debouncedNumsSearch}
								onSelect={handleNumSelect}
								{...field}
							/>
						)}
					/>
					<Controller
						name="rubber_num.o_v_or_date_of_manufacture"
						control={control}
						render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
							<CustomSelect
								{...field}
								value={value}
								onChange={onChange}
								initialValue={undefined}
								options={OVorDateSelectOptions}
								label={'σв / дата изготовления'}
								style={{ width: '100%' }}
								allowClear={false}
								isInvalid={!!error}
								onSelect={(v) => {
									onChange(v);
									setValue('rubber_num.o_v_or_date_of_manufacture', v);
								}}
							/>
						)}
					/>
					<Controller
						name="rubber_num.replace_decision"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<CustomInput
								{...field}
								label="Решение о замене материала"
								isInvalid={!!error}
								onChange={(e) => {
									setValue('rubber_num.replace_decision', e.target.value);
									field.onChange(e.target.value);
								}}
							/>
						)}
					/>
					<Controller
						name="rubber_plan.plan_count"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<CustomInput
								{...field}
								label="Введите план по количеству деталей"
								isInvalid={!!error}
								onChange={(e) => {
									setValue('rubber_plan.plan_count', Number(e.target.value.replace(/\D/g, '')));
									field.onChange(e.target.value.replace(/\D/g, ''));
								}}
							/>
						)}
					/>
					<Controller
						name="rubber_plan.issuance_fio"
						control={control}
						render={({ field, fieldState: { error } }) => (
							<CustomSelect
								{...field}
								label={'Выберите ФИО ПДС'}
								allowClear
								onClear={() => {
									setValue('rubber_plan.issuance_fio', '');
								}}
								onChange={(e) => {
									field.onChange(e);
									setValue(
										'rubber_plan.issuance_fio',
										employeeOptions.find((el) => el.value === Number(e))?.label,
										{ shouldValidate: true },
									);
								}}
								value={employeeOptions.find((el) => el.label === field.value)?.value}
								initialValue={undefined}
								options={employeeOptions}
								isInvalid={!!error}
								isLoading={isEmployeesFetching}
							/>
						)}
					/>
				</Stack>
			</form>
		);
	},
);
